import * as i2 from '@angular/router';
import { NavigationEnd, RouterModule } from '@angular/router';
import * as _ from 'lodash';
import { BehaviorSubject, switchMap, combineLatest, map, of, Subject, takeUntil } from 'rxjs';
import * as i0 from '@angular/core';
import { Injectable, Component, Input, ViewChild, NgModule } from '@angular/core';
import { BaseObservablesFeedbackComponent } from '@pl/pl-lib/common';
import * as i3 from '@progress/kendo-angular-dialog';
import { DialogsModule as DialogsModule$1 } from '@progress/kendo-angular-dialog';
import * as i3$1 from '@ngx-translate/core';
import * as i2$1 from '@angular/common';
import { CommonModule } from '@angular/common';

const defaultEntityModalSettings = {
    closeOnRouteChange: true,
    closeOnBackdropClick: true,
    closeOnSave: true
};
class BaseEntityModalService {
    translationService;
    modals$ = new BehaviorSubject([]);
    constructor(router, translationService) {
        this.translationService = translationService;
        router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                this.modals$.value
                    .filter((r) => r.options?.closeOnRouteChange)
                    .forEach((e) => e.ref.close());
            }
        });
    }
    configureModalTitle(modalRef, component, entity) {
        if (!component) {
            return;
        }
        const instance = modalRef.dialog
            ? modalRef.dialog.instance
            : modalRef.window.instance;
        if (!entity) {
            instance.title = '';
            return;
        }
        this.translate(component.displayDataResolver.getShortTitle(entity));
    }
    openFor(entity, detailComponent, options) {
        const modalOptions = (options ? { ...options } : {});
        if (modalOptions.title) {
            modalOptions.title = this.translate(modalOptions.title);
        }
        const dialogRef = this.baseOpenFunction(detailComponent, (c, o) => this.openModalFor(entity, c, o), modalOptions);
        const componentInstance = dialogRef.content?.instance;
        if (!modalOptions.title) {
            this.configureModalTitle(dialogRef, componentInstance, entity);
        }
        this.configureEntityComponent(dialogRef, componentInstance, entity, modalOptions.closeOnSave);
        return dialogRef;
    }
    openComponentModal(detailComponent, options) {
        return this.baseOpenFunction(detailComponent, (c, o) => this.openModalForComponent(c, o), options);
    }
    baseOpenFunction(detailComponent, getDialogRef, options) {
        const optionsInstance = _.assign({}, defaultEntityModalSettings, options);
        const prevClose = optionsInstance.preventClose;
        if (!prevClose) {
            optionsInstance.preventClose = () => {
                if (optionsInstance.forceClosure) {
                    return false;
                }
                const result = optionsInstance.allowRouteChangeWasGenerated
                    ? optionsInstance.allowRouteChange()
                    : true;
                if (typeof result === 'boolean') {
                    return !result;
                }
                result.then((e) => {
                    if (e) {
                        optionsInstance.forceClosure = true;
                        dialogRef.close();
                    }
                });
                return true;
            };
            optionsInstance.preventCloseWasGenerated = true;
        }
        const dialogRef = getDialogRef(detailComponent, optionsInstance);
        const canDeactivate = dialogRef.content?.instance
            ?.canDeactivate;
        if (!!canDeactivate) {
            if (optionsInstance) {
                if (!optionsInstance.allowRouteChange) {
                    optionsInstance.allowRouteChangeWasGenerated = true;
                    optionsInstance.allowRouteChange =
                        prevClose && !optionsInstance.preventCloseWasGenerated
                            ? () => !prevClose(undefined, dialogRef)
                            : canDeactivate.bind(dialogRef.content?.instance);
                }
            }
        }
        this.addToRefsArray(dialogRef, optionsInstance);
        return dialogRef;
    }
    configureEntityComponent(modalRef, component, entity, closeOnSave = true) {
        if (!component) {
            return;
        }
        component.entity = entity;
        const saveSub = component.saved.subscribe((e) => {
            if (closeOnSave) {
                modalRef.close({ saved: true, entity: e });
            }
            saveSub.unsubscribe();
            cancelSub.unsubscribe();
        });
        const cancelSub = component.cancel.subscribe(() => {
            modalRef.close({ cancelled: true });
            saveSub.unsubscribe();
            cancelSub.unsubscribe();
        });
    }
    addToRefsArray(modalRef, options) {
        const instance = modalRef.dialog || modalRef.window;
        const entityModal = { ref: modalRef, options };
        instance.onDestroy(() => this.modals$.next(this.modals$.value.filter((f) => f !== entityModal)));
        this.modals$.next(this.modals$.value.concat([entityModal]));
    }
    translate(text) {
        return text && text !== '' ? this.translationService.instant(text) : text;
    }
}

class EntityDialogService extends BaseEntityModalService {
    dialogService;
    constructor(dialogService, router, translateService) {
        super(router, translateService);
        this.dialogService = dialogService;
    }
    /**
     * @deprecated please use base-entity-modal openComponentModal method
     * @param component Will be removed in Release-0.1.0
     * @param options
     */
    openModal(component, options) {
        return this.dialogService.open(_.merge({
            content: component,
            actions: undefined,
            cssClass: 'no-padding'
        }, options));
    }
    showDeleteConfirmationDialog(entities, displayDataResolver, actionMap, errorHandler, options, confirmActionText = 'Delete') {
        const entitiesArray = Array.isArray(entities) ? entities : [entities];
        return this.openComponentModal(undefined, _.merge({
            title: `Confirm ${displayDataResolver.typeDisplayName} deletion...`,
            content: `Are you sure you want to delete ${entitiesArray.length > 1
                ? entitiesArray.length +
                    ' ' +
                    displayDataResolver.typeDisplayName
                : displayDataResolver.getShortTitle(entitiesArray[0])}?`,
            actions: [
                { text: 'Cancel' },
                { text: confirmActionText, themeColor: 'error' }
            ],
            cssClass: 'on-top-of-windows'
        }, options || {})).result.pipe(switchMap((e) => {
            if (e.text === confirmActionText) {
                const r = entitiesArray.map((i) => ({
                    text: `Deleting '${displayDataResolver.getTitle(i)}' ...`,
                    failedText: `Failed To Delete '${displayDataResolver.getTitle(i)}'.`,
                    successText: `Deleted ${displayDataResolver.getTitle(i)}.`,
                    context: i,
                    observable: actionMap(i),
                    errorHandler: (e, i) => errorHandler
                        ? errorHandler(e, i)
                        : {
                            message: {
                                details: e.error?.message || e.error || e.message || e
                            }
                        }
                }));
                if (options?.showSummaryComponent === 'never') {
                    return combineLatest(BaseObservablesFeedbackComponent.mapObservablesToResults(r));
                }
                const dialogReg = this.openComponentModal(BaseObservablesFeedbackComponent, {
                    actions: [{ text: 'Close' }],
                    cssClass: 'on-top-of-windows',
                    height: 400
                });
                const i = dialogReg.content
                    .instance;
                i.observables = r;
                i.autoClose = options?.showSummaryComponent !== 'always';
                return dialogReg.result.pipe(map((r) => i.results));
            }
            return of(e);
        }));
    }
    openModalForComponent(detailComponent, options) {
        return this.dialogService.open(_.merge({
            content: detailComponent,
            actions: undefined,
            cssClass: 'no-padding'
        }, options));
    }
    openModalFor(entity, detailComponent, options) {
        return this.openModalForComponent(detailComponent, options);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: EntityDialogService, deps: [{ token: i3.DialogService }, { token: i2.Router }, { token: i3$1.TranslateService }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: EntityDialogService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: EntityDialogService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i3.DialogService }, { type: i2.Router }, { type: i3$1.TranslateService }]; } });

class EntityWindowsService extends BaseEntityModalService {
    windowService;
    defaultTitleBarTemplate;
    // Used in pl-window-container to set the showWindowOverlay
    // If set to something and pl-window-container input is undefined it's value will be used.
    showModalBackdrop$ = new BehaviorSubject(undefined);
    constructor(windowService, router, translateService) {
        super(router, translateService);
        this.windowService = windowService;
    }
    /**
     * @deprecated please use base-entity-modal openComponentModal method
     * Will be removed in Release-0.1.0
     * @param component
     * @param options
     */
    openModal(component, options) {
        return this.windowService.open(_.merge({
            content: component,
            actions: undefined,
            cssClass: 'no-padding'
        }, options));
    }
    /**
     * Opens modal/widow without any BaseEntityDetailsComponent hooks.
     * @param detailComponent - component type - doesn't have to be BaseDetailComponent
     * @param options
     * @protected
     */
    openModalForComponent(detailComponent, options) {
        return this.windowService.open(_.merge({
            content: detailComponent,
            actions: undefined,
            cssClass: 'no-padding',
            titleBarContent: this.defaultTitleBarTemplate
        }, options));
    }
    /**
     * Opens modal for entity setting all hooks for BaseDetailComponent
     * @param entity - NOT USED AT THE MOMENT. SHOULD BE REMOVED
     * @param detailComponent
     * @param options
     * @protected
     */
    openModalFor(entity, detailComponent, options) {
        return this.openComponentModal(detailComponent, options);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: EntityWindowsService, deps: [{ token: i3.WindowService }, { token: i2.Router }, { token: i3$1.TranslateService }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: EntityWindowsService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: EntityWindowsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i3.WindowService }, { type: i2.Router }, { type: i3$1.TranslateService }]; } });

class WindowCanDeactivateGuard {
    windowService;
    constructor(windowService) {
        this.windowService = windowService;
    }
    async canDeactivate(component, route, state) {
        for (const w of this.windowService.modals$.value) {
            if (w.options?.allowRouteChange) {
                return w.options?.allowRouteChange(w, route, state);
            }
            if (w.options?.preventClose &&
                w.options?.preventClose(undefined, w.ref)) {
                return false;
            }
        }
        return true;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: WindowCanDeactivateGuard, deps: [{ token: EntityWindowsService }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: WindowCanDeactivateGuard });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: WindowCanDeactivateGuard, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: EntityWindowsService }]; } });

class WindowContainerComponent {
    entityWindowService;
    _showWindowOverlay = undefined;
    get showWindowOverlay() {
        return this._showWindowOverlay || this.entityWindowService.showModalBackdrop$.value || true;
    }
    set showWindowOverlay(value) {
        this._showWindowOverlay = value;
    }
    titleBarTemplate;
    windows;
    destroyed$ = new Subject();
    /**
     * Works with default titleBarTemplate.
     * When set will show minimize button
     */
    showMinimizeButton = false;
    /**
     * Works with default titleBarTemplate.
     * When set will show maximize button
     */
    showMaximizeButton = false;
    constructor(entityWindowService) {
        this.entityWindowService = entityWindowService;
    }
    ngOnInit() {
        this.entityWindowService.modals$
            .pipe(takeUntil(this.destroyed$))
            .subscribe((e) => {
            this.windows = e;
        });
    }
    ngAfterViewInit() {
        this.entityWindowService.defaultTitleBarTemplate = this.titleBarTemplate;
    }
    ngOnDestroy() {
        this.destroyed$.next();
    }
    getTitle(win) {
        if (!win.title) {
            win.title = this.windows.find((f) => f.ref.window.location.nativeElement === win.window.nativeElement)?.ref.window.instance.title;
        }
        return win.title;
    }
    closeWindow(window) {
        if (!window.options?.closeOnBackdropClick ||
            (window.options?.preventClose &&
                window.options?.preventClose(undefined, window.ref))) {
            return;
        }
        window.ref.close();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: WindowContainerComponent, deps: [{ token: EntityWindowsService }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: WindowContainerComponent, selector: "pl-window-container", inputs: { showWindowOverlay: "showWindowOverlay", showMinimizeButton: "showMinimizeButton", showMaximizeButton: "showMaximizeButton" }, viewQueries: [{ propertyName: "titleBarTemplate", first: true, predicate: ["windowTitleBar"], descendants: true }], ngImport: i0, template: "<ng-template #windowTitleBar let-win>\n  <div class=\"k-window-title\">{{getTitle(win)}}</div>\n  <button kendoWindowMinimizeAction [window]=\"win\" *ngIf=\"showMinimizeButton\"></button>\n  <button kendoWindowMaximizeAction [window]=\"win\" *ngIf=\"showMaximizeButton\"></button>\n  <button kendoWindowRestoreAction [window]=\"win\" *ngIf=\"showMaximizeButton || showMinimizeButton\"></button>\n  <button kendoWindowCloseAction [window]=\"win\"></button>\n</ng-template>\n\n<div class=\"k-dialog-wrapper\" *ngIf=\"showWindowOverlay && windows && windows.length>0\">\n  <div class=\"k-overlay\" (click)=\"closeWindow(windows[0])\"></div>\n</div>\n<div kendoWindowContainer></div>\n", styles: [":host ::ng-deep kendo-window{max-height:95vh}:host ::ng-deep .no-top-padding>.k-content,:host ::ng-deep .no-top-padding>.k-window-content,:host ::ng-deep .no-top-padding>.k-dialog-content,:host ::ng-deep .no-top-padding>.k-window>.k-dialog-content,:host ::ng-deep .no-top-padding>.k-dialog>.k-dialog-content,:host ::ng-deep .no-top-padding>.k-window>.k-window-content,:host ::ng-deep .no-top-padding>.k-dialog>.k-window-content{padding-top:0}:host ::ng-deep .no-padding>.k-content,:host ::ng-deep .no-padding>.k-window-content,:host ::ng-deep .no-padding>.k-dialog-content,:host ::ng-deep .no-padding>.k-window>.k-dialog-content,:host ::ng-deep .no-padding>.k-dialog>.k-dialog-content,:host ::ng-deep .no-padding>.k-window>.k-window-content,:host ::ng-deep .no-padding>.k-dialog>.k-window-content{padding:0}\n"], dependencies: [{ kind: "directive", type: i2$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i3.WindowCloseActionDirective, selector: "button[kendoWindowCloseAction]", inputs: ["window"], exportAs: ["kendoWindowCloseAction"] }, { kind: "component", type: i3.WindowMinimizeActionDirective, selector: "button[kendoWindowMinimizeAction]", inputs: ["window"], exportAs: ["kendoWindowMinimizeAction"] }, { kind: "component", type: i3.WindowMaximizeActionDirective, selector: "button[kendoWindowMaximizeAction]", inputs: ["window"], exportAs: ["kendoWindowMaximizeAction"] }, { kind: "component", type: i3.WindowRestoreActionDirective, selector: "button[kendoWindowRestoreAction]", inputs: ["window"], exportAs: ["kendoWindowRestoreAction"] }, { kind: "directive", type: i3.WindowContainerDirective, selector: "[kendoWindowContainer]" }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: WindowContainerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'pl-window-container', template: "<ng-template #windowTitleBar let-win>\n  <div class=\"k-window-title\">{{getTitle(win)}}</div>\n  <button kendoWindowMinimizeAction [window]=\"win\" *ngIf=\"showMinimizeButton\"></button>\n  <button kendoWindowMaximizeAction [window]=\"win\" *ngIf=\"showMaximizeButton\"></button>\n  <button kendoWindowRestoreAction [window]=\"win\" *ngIf=\"showMaximizeButton || showMinimizeButton\"></button>\n  <button kendoWindowCloseAction [window]=\"win\"></button>\n</ng-template>\n\n<div class=\"k-dialog-wrapper\" *ngIf=\"showWindowOverlay && windows && windows.length>0\">\n  <div class=\"k-overlay\" (click)=\"closeWindow(windows[0])\"></div>\n</div>\n<div kendoWindowContainer></div>\n", styles: [":host ::ng-deep kendo-window{max-height:95vh}:host ::ng-deep .no-top-padding>.k-content,:host ::ng-deep .no-top-padding>.k-window-content,:host ::ng-deep .no-top-padding>.k-dialog-content,:host ::ng-deep .no-top-padding>.k-window>.k-dialog-content,:host ::ng-deep .no-top-padding>.k-dialog>.k-dialog-content,:host ::ng-deep .no-top-padding>.k-window>.k-window-content,:host ::ng-deep .no-top-padding>.k-dialog>.k-window-content{padding-top:0}:host ::ng-deep .no-padding>.k-content,:host ::ng-deep .no-padding>.k-window-content,:host ::ng-deep .no-padding>.k-dialog-content,:host ::ng-deep .no-padding>.k-window>.k-dialog-content,:host ::ng-deep .no-padding>.k-dialog>.k-dialog-content,:host ::ng-deep .no-padding>.k-window>.k-window-content,:host ::ng-deep .no-padding>.k-dialog>.k-window-content{padding:0}\n"] }]
        }], ctorParameters: function () { return [{ type: EntityWindowsService }]; }, propDecorators: { showWindowOverlay: [{
                type: Input
            }], titleBarTemplate: [{
                type: ViewChild,
                args: ['windowTitleBar']
            }], showMinimizeButton: [{
                type: Input
            }], showMaximizeButton: [{
                type: Input
            }] } });

class DialogContainerComponent {
    entityDialogService;
    destroyed$ = new Subject();
    constructor(entityDialogService) {
        this.entityDialogService = entityDialogService;
    }
    ngOnInit() { }
    ngAfterViewInit() { }
    ngOnDestroy() {
        this.destroyed$.next();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: DialogContainerComponent, deps: [{ token: EntityDialogService }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: DialogContainerComponent, selector: "pl-dialog-container", ngImport: i0, template: "<div kendoDialogContainer></div>\n", styles: [":host ::ng-deep kendo-dialog{z-index:10002}:host ::ng-deep kendo-dialog>.k-dialog{max-height:95vh}:host ::ng-deep kendo-dialog.on-top-of-windows{z-index:100002}\n"], dependencies: [{ kind: "directive", type: i3.DialogContainerDirective, selector: "[kendoDialogContainer]" }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: DialogContainerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'pl-dialog-container', template: "<div kendoDialogContainer></div>\n", styles: [":host ::ng-deep kendo-dialog{z-index:10002}:host ::ng-deep kendo-dialog>.k-dialog{max-height:95vh}:host ::ng-deep kendo-dialog.on-top-of-windows{z-index:100002}\n"] }]
        }], ctorParameters: function () { return [{ type: EntityDialogService }]; } });

class DialogsModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: DialogsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: DialogsModule, declarations: [WindowContainerComponent, DialogContainerComponent], imports: [CommonModule, RouterModule, DialogsModule$1], exports: [WindowContainerComponent, DialogContainerComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: DialogsModule, providers: [WindowCanDeactivateGuard], imports: [CommonModule, RouterModule, DialogsModule$1] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: DialogsModule, decorators: [{
            type: NgModule,
            args: [{
                    providers: [WindowCanDeactivateGuard],
                    declarations: [WindowContainerComponent, DialogContainerComponent],
                    imports: [CommonModule, RouterModule, DialogsModule$1],
                    exports: [WindowContainerComponent, DialogContainerComponent]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { BaseEntityModalService, DialogContainerComponent, DialogsModule, EntityDialogService, EntityWindowsService, WindowCanDeactivateGuard, WindowContainerComponent, defaultEntityModalSettings };
